import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRoles } from "@/hooks/useRoles";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "@/lib/icons";

interface PlatformAd {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  cta_label: string;
  cta_link: string;
  target_roles: string[];
  target_plans: string[];
  exclude_roles: string[];
  starts_at: string | null;
  ends_at: string | null;
}

export function AdBanner() {
  const { user } = useAuth();
  const { roles, isAdmin, isCoach, loading: rolesLoading } = useRoles();
  const { plan, subscription, loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  const [ads, setAds] = useState<PlatformAd[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("dismissed_ads");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const impressionLogged = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;
    supabase
      .from("platform_ads")
      .select("id,title,description,image_url,cta_label,cta_link,target_roles,target_plans,exclude_roles,starts_at,ends_at")
      .eq("status", "active")
      .order("sort_order", { ascending: true })
      .then(({ data }) => setAds((data as PlatformAd[]) || []));
  }, [user]);

  const trackEvent = useCallback((adId: string, eventType: string) => {
    if (!user) return;
    supabase.from("ad_events").insert({
      ad_id: adId,
      user_id: user.id,
      event_type: eventType,
    }).then(() => {}); // fire-and-forget
  }, [user]);

  if (!user || rolesLoading || subLoading) return null;
  if (isAdmin || isCoach) return null;

  const now = new Date();
  const userRoles = roles.length > 0 ? roles : ["user"];
  const userPlan = plan || "free";

  const visibleAds = ads.filter((ad) => {
    if (dismissed.has(ad.id)) return false;
    if (ad.starts_at && new Date(ad.starts_at) > now) return false;
    if (ad.ends_at && new Date(ad.ends_at) < now) return false;
    if (ad.exclude_roles.some((r) => userRoles.includes(r as any))) return false;
    if (ad.target_roles.length > 0 && !ad.target_roles.some((r) => userRoles.includes(r as any))) return false;
    if (ad.target_plans.length > 0 && !ad.target_plans.includes(userPlan)) return false;
    return true;
  });

  if (visibleAds.length === 0) return null;

  const ad = visibleAds[0];

  // Log impression once per ad per component lifecycle
  if (!impressionLogged.current.has(ad.id)) {
    impressionLogged.current.add(ad.id);
    trackEvent(ad.id, "impression");
  }

  const dismiss = (id: string) => {
    trackEvent(id, "dismissal");
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    localStorage.setItem("dismissed_ads", JSON.stringify(Array.from(next)));
  };


  const handleClick = (e: React.MouseEvent, ad: PlatformAd) => {
    e.stopPropagation();
    trackEvent(ad.id, "click");
    navigate(ad.cta_link);
  };

  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-start gap-3">
      {ad.image_url && (
        <img src={ad.image_url} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-card-foreground">{ad.title}</p>
        {ad.description && <p className="text-xs text-muted-foreground mt-0.5">{ad.description}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" className="text-xs" onClick={(e) => handleClick(e, ad)}>
          {ad.cta_label}
        </Button>
        <button onClick={() => dismiss(ad.id)} className="text-muted-foreground hover:text-foreground p-1">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
